"use client";

import clsx from "clsx";
import useEmblaCarousel from "embla-carousel-react";
import type { ReactNode } from "react";
import { Children, useCallback, useEffect, useState } from "react";

import { ChevronRightIcon } from "../../../_components/dx-icons";

import { DxIconButton } from "./dx-icon-button";

/**
 * 横スクロールカルーセル。 PC hover で送り矢印、 SP は touch swipe に任せて矢印非表示。
 * slide 幅は呼び出し側が slideClassName で渡す (section ごとに違うため atom 側では固定しない)。
 */
type DxCarouselProps = {
  children: ReactNode;
  /** スクリーンリーダー用 region label */
  ariaLabel: string;
  slideClassName?: string;
  gapClassName?: string;
  className?: string;
};

export function DxCarousel({
  children,
  ariaLabel,
  slideClassName,
  gapClassName = "gap-[12px]",
  className,
}: DxCarouselProps) {
  const [emblaRef, emblaApi] = useEmblaCarousel({
    align: "start",
    containScroll: "trimSnaps",
    skipSnaps: false,
  });
  const [canPrev, setCanPrev] = useState(false);
  const [canNext, setCanNext] = useState(false);

  useEffect(() => {
    if (emblaApi === undefined) return;
    const update = () => {
      setCanPrev(emblaApi.canScrollPrev());
      setCanNext(emblaApi.canScrollNext());
    };
    update();
    emblaApi.on("reInit", update).on("select", update);
  }, [emblaApi]);

  const scrollPrev = useCallback(() => emblaApi?.scrollPrev(), [emblaApi]);
  const scrollNext = useCallback(() => emblaApi?.scrollNext(), [emblaApi]);

  const slides = Children.toArray(children);

  return (
    // named group `carousel` で内包する DxCarouselItem の group/item と名前空間を分離。
    // 単に group だと隣カードの hover まで発火してしまう。
    <div
      className={clsx("group/carousel relative", className)}
      role="region"
      aria-label={ariaLabel}
    >
      <div className="overflow-hidden" ref={emblaRef}>
        <div className={clsx("flex", gapClassName)}>
          {slides.map((child, i) => (
            <div key={i} className={clsx("min-w-0 flex-none", slideClassName)}>
              {child}
            </div>
          ))}
        </div>
      </div>
      <div className="xs:flex pointer-events-none absolute inset-y-0 left-0 hidden items-center">
        {canPrev && (
          <div className="pointer-events-auto opacity-0 transition-opacity duration-150 group-hover/carousel:opacity-100">
            <DxIconButton
              variant="float"
              size="xsmall"
              ariaLabel="前へ"
              // カルーセルの送りだけで、行動の分析対象にならない
              data-analytics-ignore
              onClick={scrollPrev}
              icon={<ChevronRightIcon className="rotate-180" />}
            />
          </div>
        )}
      </div>
      <div className="xs:flex pointer-events-none absolute inset-y-0 right-0 hidden items-center">
        {canNext && (
          <div className="pointer-events-auto opacity-0 transition-opacity duration-150 group-hover/carousel:opacity-100">
            <DxIconButton
              variant="float"
              size="xsmall"
              ariaLabel="次へ"
              // カルーセルの送りだけで、行動の分析対象にならない
              data-analytics-ignore
              onClick={scrollNext}
              icon={<ChevronRightIcon />}
            />
          </div>
        )}
      </div>
    </div>
  );
}
